import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { AnalyticsOrigin } from '../../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';

/**
 * Returns handleClose(attributes) that fires analytics and calls onClose.
 * Pass attributes including outcome (e.g. MODAL_OUTCOME.CANCELLED, MODAL_OUTCOME.CREATED) and any extra fields (e.g. snippetId).
 * actionSubjectId (from hook args) and outcome identify the close event.
 *
 * @param onClose - Callback when the modal closes
 * @param origin - Analytics origin for this modal (e.g. CREATE_SNIPPET_MODAL, EDIT_SNIPPET_MODAL)
 * @param actionSubjectId - UI subject ID for this modal (e.g. CREATE_SNIPPET, EDIT_SNIPPET, DUPLICATE_SNIPPET)
 */
export function useSnippetModalClose(
	onClose: () => void,
	origin: AnalyticsOrigin,
	actionSubjectId: string,
): (attributes: Record<string, unknown>) => void {
	const { fireModalClosedAnalytic } = useSnippetAnalytics(origin);
	const { userId, cloudId } = useSessionData();

	const baseAnalyticsAttributes: Record<string, unknown> = {
		...(userId != null && { userId }),
		...(cloudId != null && { cloudId }),
		scope: SnippetScope.USER,
	};

	return (attributes: Record<string, unknown>) => {
		fireModalClosedAnalytic(actionSubjectId, {
			...baseAnalyticsAttributes,
			...attributes,
		});
		onClose();
	};
}
